// BM-H81 P2 - ClientFormLPA page (tokenised, unauthenticated).
//
// Route: /client-forms/lpas/:form_instance_id
// Query: ?token=<jwt>
//
// Loads the LPA form instance, renders 10 sections with live engine
// feedback from the LPA engine bundle. Uses the shared FormShell,
// EngineResultView, TokenGuard, HaikuAssistantPanel components that
// wills P2 authored.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { clientFormsApi } from "../api/clientForms";
import { errorMessage } from "../utils/errorMessage";
import CapacityConversationPanel from "../components/client-forms/CapacityConversationPanel";
import EngineResultView from "../components/client-forms/EngineResultView";
import { toClientEngineView } from "../components/client-forms/lpa/lpaClientMessages";
import FormShell from "../components/client-forms/FormShell";
import HaikuAssistantPanel from "../components/client-forms/HaikuAssistantPanel";
import LPAFormSections, { LPA_SECTIONS } from "../components/client-forms/LPAFormSections";
import TokenGuard from "../components/client-forms/TokenGuard";

function useDebouncedCallback(fn, delay) {
  const timerRef = useRef(null);
  return useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay]
  );
}

// BM-H4141: the engine's result is kept as it came back and turned into the client's own words at render
// time (lpaClientMessages.js), naming people from the answers as they stand. The engine's staff wording,
// statute references and rule codes stay on the fee earner's View submission page.
const LIVE_CHECK_TOAST_ID = "lpa-live-check";
// States in which the donor's answers are locked.
const SUBMITTED_STATES = ["submitted", "under_review", "finalised"];

export default function ClientFormLPA() {
  const { form_instance_id: formId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const token = searchParams.get("token");
  // BM-H3573 - found live in the real browser immediately after the first
  // ever successful LPA submission this session: handleSubmit navigated to
  // `/thanks` without the token query string, so TokenGuard (which gates
  // this whole page, see below) showed every client "This link is missing
  // its access token" right after they finished their LPA, instead of a
  // thank-you page. There was also no distinct thank-you view at all - even
  // with the token preserved, the client would have been dropped straight
  // back onto the full ten-section form. ClientFormWills.jsx already has
  // both the token-preserving navigate and a submittedView; this mirrors
  // that pattern.
  const submittedView = pathname.endsWith("/thanks");

  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(Boolean(token) && !submittedView);
  const [loadError, setLoadError] = useState(null);
  const [activeSection, setActiveSection] = useState(LPA_SECTIONS[0].id);
  const [engineRaw, setEngineRaw] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentFieldId, setCurrentFieldId] = useState(null);
  const [capacityValue, setCapacityValue] = useState({});
  const instrumentRef = useRef(null);

  const activeIdx = useMemo(
    () => LPA_SECTIONS.findIndex((s) => s.id === activeSection),
    [activeSection]
  );

  useEffect(() => {
    if (!token || submittedView) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await clientFormsApi.getForm(formId, token);
        if (cancelled) return;
        // BM-H3715: once submitted the answers are locked (the server refuses any change),
        // so the link opens the plain submitted page, never the editable form.
        if (SUBMITTED_STATES.includes((data?.state || "").toLowerCase())) {
          navigate(`/client-forms/lpas/${formId}/thanks?token=${encodeURIComponent(token)}`, { replace: true });
          return;
        }
        setInstance(data);
        const rawResult = data?.engine_result?.result || null;
        setEngineRaw(rawResult);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError(errorMessage(err, "Could not load the form."));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formId, token, submittedView, navigate]);

  const dryRun = useDebouncedCallback(async (instrument) => {
    if (!token) return;
    try {
      const { data } = await clientFormsApi.patchAnswers(
        formId,
        token,
        { instrument, capacity_review: capacityValue },
        true
      );
      // BM-H3545 - patch_answers returns 200 even when the engine itself
      // failed (engine_result: null, engine_error: <message>), so a thrown
      // exception alone never caught this case; the live-checks panel went
      // silently blank with no toast at all, for every single dry run
      // before this fix (engine_error was always set, since the engine
      // could not run without signature_bundle). Still rare after the fix,
      // but a real failure must surface, not disappear.
      if (data?.engine_error) {
        // BM-H3706: one stable id, so a repeat replaces the pop-up instead of stacking
        // another on top (eleven were stacked by the last section in a browser run).
        toast.error(`Live check failed: ${data.engine_error}`, { id: LIVE_CHECK_TOAST_ID });
        return;
      }
      const raw = data?.engine_result?.result || data?.engine_result || null;
      setEngineRaw(raw);
    } catch (err) {
      toast.error(`Live check failed: ${errorMessage(err, "unknown error")}`, { id: LIVE_CHECK_TOAST_ID });
    }
  }, 700);

  function handleInstrumentChange(instrument) {
    instrumentRef.current = instrument;
    dryRun(instrument);
  }

  function handleCapacityChange(next) {
    setCapacityValue(next);
    if (instrumentRef.current) dryRun(instrumentRef.current);
  }

  async function handleAskHaiku({ mode, question, fieldId, parkAsSuggestion }) {
    const { data } = await clientFormsApi.askHaiku(formId, token, {
      mode,
      question,
      fieldId,
      parkAsSuggestion,
    });
    return data;
  }

  function nextSection() {
    if (activeIdx < LPA_SECTIONS.length - 1) {
      setActiveSection(LPA_SECTIONS[activeIdx + 1].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function prevSection() {
    if (activeIdx > 0) {
      setActiveSection(LPA_SECTIONS[activeIdx - 1].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      if (instrumentRef.current) {
        await clientFormsApi.patchAnswers(
          formId,
          token,
          { instrument: instrumentRef.current, capacity_review: capacityValue },
          false
        );
      }
      const { data } = await clientFormsApi.submitForm(formId, token);
      setEngineRaw(data?.engine_result || null);
      // BM-H3706: nothing from the live checks should still be on screen over the thank-you page.
      toast.dismiss(LIVE_CHECK_TOAST_ID);
      toast.success("Thank you. Your LPA questionnaire has been submitted for review.");
      navigate(`/client-forms/lpas/${formId}/thanks?token=${encodeURIComponent(token)}`, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.hard_stops) {
        toast.error("Your answers still contain items we need to resolve. Please review the red callouts.");
        setActiveSection("s10_review");
      } else {
        toast.error(errorMessage(err, "Submission failed"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  // BM-H3576 - this used to be a bare success toast with no request behind it.
  // It now says a fee earner will call only when the backend wrote a task or
  // sent an email for them.
  async function handleRequestCallback() {
    const hardStopCodes = (engineRaw?.hard_stops || []).map((h) => h.code).filter(Boolean);
    try {
      await clientFormsApi.requestCallback(formId, token, { section: activeSection, hardStopCodes });
      toast.success("Thanks. We have asked a fee earner to call you.");
    } catch (err) {
      toast.error(errorMessage(err, "We could not pass your request on just now. Please try again in a few minutes, or phone us."));
    }
  }

  if (!token) return <TokenGuard token={null} />;
  if (submittedView) {
    return (
      <div data-testid="client-form-submitted" style={{ maxWidth: 640, margin: "60px auto", padding: "0 20px", lineHeight: 1.6 }}>
        <h1 style={{ fontSize: 24, margin: "0 0 12px" }}>Thank you. Your LPA questionnaire has been submitted.</h1>
        <p style={{ fontSize: 15 }}>Your fee earner will review your answers and prepare the LPA document for you to print and sign. Nothing is final until the printed document has been signed and witnessed, and OPG has registered it.</p>
        <p style={{ fontSize: 15 }}>If anything in your answers needs discussing first, your fee earner will pick it up with you. You can close this tab.</p>
        <p style={{ fontSize: 15 }}>Your answers are now locked. If you need to change something, ask your fee earner. They can open the questionnaire up again for you.</p>
      </div>
    );
  }
  if (loading) return <div data-testid="lpa-form-loading" style={{ padding: 60, textAlign: "center" }}>Loading your questionnaire...</div>;
  if (loadError) return <div data-testid="lpa-form-load-error" style={{ padding: 60, textAlign: "center", color: "#b91c1c" }}>{loadError}</div>;

  const engineResult = toClientEngineView(engineRaw, instrumentRef.current || instance?.answers?.instrument);
  const hardStopCount = (engineResult?.flags?.hard_stops || []).length;
  const lpaKind = instance?.lpa_kind || "hw";

  const rightRail = (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <HaikuAssistantPanel onAsk={handleAskHaiku} currentFieldId={currentFieldId} />
      <div
        data-testid="lpa-mini-engine-panel"
        style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, background: "#fff" }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8, color: "#0f172a", fontSize: 14 }}>
          Live checks
        </div>
        <EngineResultView engineResult={engineResult} compact hideCodes />
      </div>
    </div>
  );

  return (
    <TokenGuard token={token}>
      <FormShell
        matterRef={instance?.matter_ref || ""}
        clientName={instance?.client_name}
        sections={LPA_SECTIONS}
        activeSection={activeSection}
        onSaveAndReturn={() => toast.success("Progress saved.")}
        onRequestCallback={handleRequestCallback}
        rightRail={rightRail}
        questionnaireLabel={lpaKind === "hw" ? "LPA questionnaire (health and welfare)" : "LPA questionnaire (property and financial affairs)"}
        documentNoun="LPA"
      >
        <div data-testid="lpa-kind-badge" style={{ marginBottom: 12, fontSize: 12, color: "#64748b" }}>
          Instrument: {lpaKind === "hw" ? "LP1H (Health and Welfare)" : "LP1F (Property and Financial Affairs)"}
        </div>

        <LPAFormSections
          activeSection={activeSection}
          // BM-H3545 - this was always {lpa_kind: lpaKind}, never
          // including instance.answers.instrument. Unlike ClientFormWills
          // (which explicitly hydrates from instance.answers "so a save
          // and return" works), every reload of this page silently
          // discarded all previously-entered donor/attorney/etc data from
          // the client's view - answers were saved server-side, but never
          // shown back. Saved data now takes priority; lpa_kind/lpa_type
          // remain as a fallback for an instrument that has neither yet.
          defaultInstrument={{ lpa_kind: lpaKind, lpa_type: lpaKind, ...(instance?.answers?.instrument || {}) }}
          onInstrumentChange={handleInstrumentChange}
          onFocusField={setCurrentFieldId}
          reviewPanel={<EngineResultView engineResult={engineResult} hideCodes />}
          capacityPanel={
            <CapacityConversationPanel value={capacityValue} onChange={handleCapacityChange} />
          }
          lpaKind={lpaKind}
        />

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32 }}>
          <button
            type="button"
            data-testid="lpa-prev-btn"
            onClick={prevSection}
            disabled={activeIdx === 0}
            style={{
              padding: "10px 18px",
              borderRadius: 6,
              border: "1px solid #94a3b8",
              background: "#fff",
              color: "#334155",
              cursor: activeIdx === 0 ? "not-allowed" : "pointer",
              opacity: activeIdx === 0 ? 0.5 : 1,
              fontWeight: 600,
            }}
          >
            Back
          </button>
          {activeSection !== "s10_review" ? (
            <button
              type="button"
              data-testid="lpa-next-btn"
              onClick={nextSection}
              style={{
                padding: "10px 18px",
                borderRadius: 6,
                border: "1px solid #4338ca",
                background: "#4338ca",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Save and continue
            </button>
          ) : (
            <button
              type="button"
              data-testid="lpa-submit-btn"
              onClick={handleSubmit}
              disabled={hardStopCount > 0 || submitting}
              style={{
                padding: "10px 18px",
                borderRadius: 6,
                border: "none",
                background: hardStopCount > 0 || submitting ? "#94a3b8" : "#4338ca",
                color: "#fff",
                cursor: hardStopCount > 0 || submitting ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              {submitting
                ? "Submitting..."
                : hardStopCount > 0
                  ? `Resolve ${hardStopCount} item${hardStopCount === 1 ? "" : "s"} to submit`
                  : "Submit questionnaire"}
            </button>
          )}
        </div>
      </FormShell>
    </TokenGuard>
  );
}
